import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import {
  Box,
  Heading,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Tag,
  Alert,
  AlertIcon,
  Progress,
  VStack,
  HStack,
  Text,
  useDisclosure,
  useToast,
  Badge,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Divider
} from '@chakra-ui/react';
import { DownloadIcon, RepeatIcon } from '@chakra-ui/icons';
import { format, isValid, parseISO } from 'date-fns';
import { calculatorImportService } from '../../services/calculatorImport.service';

// Format phone number to display in a readable format
const formatPhoneNumber = (phone) => {
  if (!phone) return '-';
  
  // For display purposes, just clean up any extra spaces but keep the format
  // including "+" for international numbers
  return phone.toString().trim();
};

// Format date to readable string
const formatDate = (dateString, formatStr = 'PPpp') => {
  if (!dateString) return 'N/A';
  try {
    // Parse ISO string to Date object
    const date = parseISO(dateString);
    return isValid(date) ? format(date, formatStr) : 'Invalid date';
  } catch (error) {
    console.error('Date parsing error:', error);
    return 'Invalid date';
  }
};

const ImportCalculatorLeads = forwardRef(({ loading, setLoading }, ref) => {
  const [importResults, setImportResults] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [submissionCount, setSubmissionCount] = useState(null);
  const [newLeadsCount, setNewLeadsCount] = useState(null);
  const [countLoading, setCountLoading] = useState(false);
  const toast = useToast();

  // Expose fetchSubmissionCount method to parent component
  useImperativeHandle(ref, () => ({
    fetchSubmissionCount
  }));

  // Fetch submission count on component mount
  useEffect(() => {
    let isMounted = true;
    
    const fetchData = async () => {
      if (isMounted) {
        await fetchSubmissionCount();
      }
    };
    
    fetchData();
    
    // Cleanup function
    return () => {
      isMounted = false;
      // Close modal on unmount to prevent scrollTop errors
      setShowResults(false);
    };
  }, []);

  const fetchSubmissionCount = async () => {
    try {
      setCountLoading(true);
      const [totalCount, newCount] = await Promise.all([
        calculatorImportService.getSubmissionCount(),
        calculatorImportService.getNewLeadsCount()
      ]);
      
      setSubmissionCount(totalCount);
      setNewLeadsCount(newCount);
    } catch (error) {
      console.error('Error fetching calculator submission count:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch calculator submission count',
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    } finally {
      setCountLoading(false);
    }
  };

  const handleImport = async () => {
    try {
      setLoading(true);
      const results = await calculatorImportService.importLeads();
      setImportResults(results);
      setShowResults(true);
      toast({
        title: 'Import Successful',
        description: `Imported ${results.imported} leads from calculator submissions`,
        status: 'success',
        duration: 5000,
        isClosable: true
      });
      // Refresh count after import
      fetchSubmissionCount();
    } catch (error) {
      console.error('Import failed:', error);
      toast({
        title: 'Import Failed',
        description: error.response?.data?.message || error.message || 'An error occurred during import',
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    } finally {
      setLoading(false);
    }
  };

  const renderResultsTable = () => {
    if (!importResults) return null;

    const results = importResults;

    return (
      <VStack spacing={6} align="stretch">
        <Box>
          <Heading size="sm" mb={4}>
            Import Summary
          </Heading>
          <HStack spacing={4}>
            <Tag size="lg" variant="subtle">
              Total: {results.total}
            </Tag>
            <Tag size="lg" colorScheme="green">
              Imported: {results.imported}
            </Tag>
            <Tag size="lg" colorScheme="yellow">
              Skipped: {results.skipped}
            </Tag>
            <Tag size="lg" colorScheme="red">
              Errors: {results.errors.length}
            </Tag>
          </HStack>
        </Box>

        {results.errors.length > 0 && (
          <Box>
            <Heading size="sm" mb={4}>
              Errors
            </Heading>
            <Table variant="simple" size="sm">
              <Thead>
                <Tr>
                  <Th>Phone Number</Th>
                  <Th>Error</Th>
                </Tr>
              </Thead>
              <Tbody>
                {results.errors.map((error, index) => (
                  <Tr key={index}>
                    <Td>{error.phone}</Td>
                    <Td>{error.error}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        )}

        {results.importedLeads.length > 0 && (
          <Box>
            <Heading size="sm" mb={4}>
              Imported Leads
            </Heading>
            <Table variant="simple" size="sm">
              <Thead>
                <Tr>
                  <Th>Lead ID</Th>
                  <Th>Name</Th>
                  <Th>Phone</Th>
                  <Th>Venues</Th>
                </Tr>
              </Thead>
              <Tbody>
                {results.importedLeads.map((lead) => (
                  <Tr key={lead.leadId}>
                    <Td>{lead.leadId}</Td>
                    <Td>{lead.brideAndGroomName || '-'}</Td>
                    <Td>{formatPhoneNumber(lead.phone)}</Td>
                    <Td>
                      {lead.locations?.venues?.length > 0 ? lead.locations.venues.join(', ') : '-'}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        )}
      </VStack>
    );
  };

  return (
    <Box bg="white" p={6} borderRadius="md" shadow="sm" mb={6}>
      <HStack justify="space-between" mb={4}>
        <Heading size="md">
          Import Leads from Wedding Calculator
        </Heading>
        
        <HStack>
          {submissionCount !== null && newLeadsCount !== null && (
            <VStack align="flex-end" spacing={0}>
              <Stat textAlign="right" size="sm">
                <StatLabel>Available Submissions</StatLabel>
                <StatNumber>{submissionCount}</StatNumber>
              </Stat>
              <Stat textAlign="right" size="sm">
                <StatLabel>New Leads to Import</StatLabel>
                <StatNumber>{newLeadsCount}</StatNumber>
                <StatHelpText>Ready to import</StatHelpText>
              </Stat>
            </VStack>
          )}
          <Button 
            size="sm" 
            leftIcon={<RepeatIcon />} 
            onClick={fetchSubmissionCount}
            isLoading={countLoading}
            variant="outline"
          >
            Refresh
          </Button>
        </HStack>
      </HStack>
      
      <Divider my={4} />
      
      <Text mb={4}>
        This will import all leads from the wedding calculator submissions into the CRM system.
        {newLeadsCount === 0 && (
          <Alert status="info" mt={2}>
            <AlertIcon />
            No new calculator submissions are currently available for import.
          </Alert>
        )}
      </Text>

      <Button
        colorScheme="teal"
        leftIcon={<DownloadIcon />}
        onClick={handleImport}
        isDisabled={loading || newLeadsCount === 0}
        mb={4}
        size="lg"
      >
        Import Calculator Submissions
      </Button>

      {loading && (
        <Progress size="xs" isIndeterminate mt={4} />
      )}

      {/* Show last import summary if available */}
      {importResults && !showResults && (
        <Box mt={6} p={4} borderWidth="1px" borderRadius="md" bg="gray.50">
          <Heading size="sm" mb={3}>Last Import Summary</Heading>
          <HStack spacing={4} mb={2}>
            <Tag size="md" variant="subtle">
              Total: {importResults.total}
            </Tag>
            <Tag size="md" colorScheme="green">
              Imported: {importResults.imported}
            </Tag>
            <Tag size="md" colorScheme="yellow">
              Skipped: {importResults.skipped}
            </Tag>
            <Tag size="md" colorScheme="red">
              Errors: {importResults.errors.length}
            </Tag>
          </HStack>
          <Button 
            size="sm" 
            mt={2} 
            onClick={() => setShowResults(true)}
            variant="outline"
          >
            View Details
          </Button>
        </Box>
      )}

      <Modal
        isOpen={showResults}
        onClose={() => setShowResults(false)}
        size="xl"
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Calculator Import Results</ModalHeader>
          <ModalBody>
            {renderResultsTable()}
          </ModalBody>
          <ModalFooter>
            <Button onClick={() => setShowResults(false)}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
});

export default ImportCalculatorLeads;
